import React, { useState, useEffect, useMemo } from 'react';
import { jobsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ChevronLeft, ChevronRight, CalendarDays, Building2, Hash, MapPin, Users,
  Briefcase, CalendarClock, ArrowUpRight, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday,
} from 'date-fns';

const statusColor = (s) => ({
  pending: 'bg-amber-400',
  in_progress: 'bg-blue-400',
  completed: 'bg-emerald-400',
  on_hold: 'bg-orange-400',
  cancelled: 'bg-rose-400',
}[s] || 'bg-primary');

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const BookingCalendarPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [tradeFilter, setTradeFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');

  useEffect(() => {
    jobsAPI.getAll()
      .then((res) => setJobs((res.data || []).filter((j) => j.on_site_date)))
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => setLoading(false));
  }, []);

  const safeDate = (d) => {
    try { return parseISO(d); } catch { return null; }
  };

  // Unique trades & suppliers for the filter dropdowns
  const tradeOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => (j.trades_required || []).forEach((t) => t && set.add(t)));
    return Array.from(set).sort();
  }, [jobs]);

  const supplierOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => j.supplier_name && set.add(j.supplier_name));
    return Array.from(set).sort();
  }, [jobs]);

  // Apply trade / supplier filters
  const filteredJobs = useMemo(() => {
    return jobs.filter((j) => {
      const matchTrade = tradeFilter === 'all' || (j.trades_required || []).includes(tradeFilter);
      const matchSupplier = supplierFilter === 'all' || j.supplier_name === supplierFilter;
      return matchTrade && matchSupplier;
    });
  }, [jobs, tradeFilter, supplierFilter]);

  // Map YYYY-MM-DD -> jobs[]
  const bookingsByDay = useMemo(() => {
    const map = {};
    filteredJobs.forEach((j) => {
      const key = (j.on_site_date || '').slice(0, 10);
      if (!key) return;
      (map[key] = map[key] || []).push(j);
    });
    return map;
  }, [filteredJobs]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const upcoming = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return filteredJobs
      .filter((j) => { const d = safeDate(j.on_site_date); return d && d >= today; })
      .sort((a, b) => new Date(a.on_site_date) - new Date(b.on_site_date))
      .slice(0, 6);
  }, [filteredJobs]);

  const jobsForDay = (day) => bookingsByDay[format(day, 'yyyy-MM-dd')] || [];
  const selectedJobs = selectedDay ? jobsForDay(selectedDay) : [];

  return (
    <div className="space-y-5 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-primary" /> Booking Calendar
          </h1>
          <p className="text-muted-foreground mt-1">Every job plotted by its on-site date — never miss a booking</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor(subMonths(cursor, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5" /> Filter bookings
        </span>
        <Select value={tradeFilter} onValueChange={setTradeFilter}>
          <SelectTrigger className="w-full sm:w-[200px] bg-input border-border rounded-lg" data-testid="cal-trade-filter">
            <SelectValue placeholder="All trades" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border max-h-72">
            <SelectItem value="all">All trades</SelectItem>
            {tradeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger className="w-full sm:w-[220px] bg-input border-border rounded-lg" data-testid="cal-supplier-filter">
            <SelectValue placeholder="All suppliers" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border max-h-72">
            <SelectItem value="all">All suppliers</SelectItem>
            {supplierOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {(tradeFilter !== 'all' || supplierFilter !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setTradeFilter('all'); setSupplierFilter('all'); }} data-testid="cal-clear-filters">
            Clear
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        {/* Calendar */}
        <Card className="surface xl:col-span-3">
          <CardContent className="p-4 sm:p-5">
            <h2 className="text-xl font-bold font-heading mb-4">{format(cursor, 'MMMM yyyy')}</h2>
            {loading ? (
              <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                  {WEEKDAYS.map((d) => (
                    <div key={d} className="text-center text-xs font-semibold text-muted-foreground/70 uppercase tracking-wide py-1">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1 sm:gap-2">
                  {days.map((day) => {
                    const dayJobs = jobsForDay(day);
                    const inMonth = isSameMonth(day, cursor);
                    const today = isToday(day);
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => dayJobs.length && setSelectedDay(day)}
                        className={`min-h-[84px] sm:min-h-[96px] rounded-xl border p-1.5 text-left transition-all ${
                          inMonth ? 'border-border bg-background/30' : 'border-transparent bg-transparent opacity-40'
                        } ${dayJobs.length ? 'hover:border-primary/50 cursor-pointer' : 'cursor-default'} ${today ? 'ring-1 ring-primary' : ''}`}
                        data-testid={`cal-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-semibold h-6 w-6 flex items-center justify-center rounded-full ${today ? 'brand-gradient text-white' : 'text-muted-foreground'}`}>
                            {format(day, 'd')}
                          </span>
                          {dayJobs.length > 0 && (
                            <span className="text-[10px] font-bold text-primary">{dayJobs.length}</span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {dayJobs.slice(0, 2).map((j) => (
                            <div key={j.id} className="flex items-center gap-1 px-1 py-0.5 rounded bg-secondary/70">
                              <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${statusColor(j.status)}`} />
                              <span className="text-[10px] truncate">{j.supplier_name || j.client_name || j.title}</span>
                            </div>
                          ))}
                          {dayJobs.length > 2 && (
                            <div className="text-[10px] text-muted-foreground pl-1">+{dayJobs.length - 2} more</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Upcoming sidebar */}
        <Card className="surface">
          <CardContent className="p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-4"><CalendarClock className="h-4 w-4 text-primary" /> Upcoming bookings</h3>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming bookings. Import a PO or add an on-site date to a job.</p>
            ) : (
              <div className="space-y-3">
                {upcoming.map((j) => (
                  <button key={j.id} onClick={() => navigate('/jobs')} className="w-full text-left group" data-testid={`upcoming-${j.id}`}>
                    <div className="flex items-start gap-3 p-2 rounded-xl hover:bg-secondary/60 transition-colors">
                      <div className="text-center flex-shrink-0 w-11">
                        <p className="text-[10px] uppercase text-muted-foreground">{format(parseISO(j.on_site_date), 'MMM')}</p>
                        <p className="text-lg font-bold leading-none">{format(parseISO(j.on_site_date), 'd')}</p>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate group-hover:text-primary">{j.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{j.supplier_name || j.client_name}</p>
                        {(j.trades_required || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {j.trades_required.slice(0, 3).map((t) => (
                              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded border border-border">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Day detail dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDay && format(selectedDay, 'EEEE, d MMMM yyyy')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedJobs.map((j) => (
              <div key={j.id} className="surface p-4">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold">{j.title}</h4>
                  <Badge className={`${j.status ? `status-${j.status}` : ''} rounded-lg text-xs capitalize`}>{(j.status || 'pending').replace('_', ' ')}</Badge>
                </div>
                <div className="grid grid-cols-1 gap-1.5 mt-3 text-sm">
                  {j.supplier_name && <Row icon={Building2} text={j.supplier_name} />}
                  {j.po_reference && <Row icon={Hash} text={j.po_reference} mono />}
                  {j.address && <Row icon={MapPin} text={j.address} />}
                  {j.client_name && <Row icon={Briefcase} text={j.client_name} />}
                </div>
                {(j.trades_required || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {j.trades_required.map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                  </div>
                )}
                <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/jobs')}>
                  Open job <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ icon: Icon, text, mono }) => (
  <div className="flex items-center gap-2 text-muted-foreground">
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span className={mono ? 'font-mono text-foreground' : 'text-foreground'}>{text}</span>
  </div>
);

export default BookingCalendarPage;
